/**
 * How long a post is on each network, and how long it may be (Prototype v14,
 * Write a post: "X 93 / 280 · Threads 93 / 500" under the text).
 *
 *   X          280; a link always counts as 23 (t.co), plus the space before it;
 *              emoji, "…" and most non-Latin characters count as 2
 *   Facebook   63,206; a link in the post counts, unless a photo or video carries
 *              it: that goes in the first comment
 *   Instagram, YouTube, TikTok  the link joins the text, tracking tags and all
 *   Bluesky    300 graphemes; a link goes in a card and doesn't count, unless the
 *              post has a photo or video: then it joins the text, as above
 *   Threads    500, an emoji being its UTF-8 bytes; the link as on Bluesky
 *   LinkedIn   3,000; the link as on Bluesky (a link preview's goes in its card)
 *   the rest   the link counts in full, plus a space
 */
export const POST_LIMITS: Record<string, number> = {
  x: 280,
  threads: 500,
  linkedin: 3000,
  instagram: 2200,
  facebook: 63206,
  bluesky: 300,
  youtube: 5000,
  tiktok: 2200,
};

/** One key per network ('twitter' rows are X). */
export const networkKey = (platform: string) => {
  const p = (platform || '').toLowerCase();
  return p === 'twitter' ? 'x' : p;
};

export const postLimit = (platform: string): number | null =>
  POST_LIMITS[networkKey(platform)] ?? null;

/**
 * Words as X counts them. Keep identical in smagent-engine utils/xLimit.ts, which
 * refuses a post over the limit, and smagent-apiserver reach/services/postWords.js.
 */
function xLength(text: string): number {
  const body = text.normalize('NFC').replace(/https?:\/\/\S+/g, 'x'.repeat(23));
  let n = 0;
  for (const ch of body) {
    const c = ch.codePointAt(0) as number;
    const one =
      c <= 4351 || (c >= 8192 && c <= 8205) || (c >= 8208 && c <= 8223) || (c >= 8242 && c <= 8247);
    n += one ? 1 : 2;
  }
  return n;
}

/** What a reader sees as one character each, where the browser can tell. */
function graphemes(text: string): string[] {
  const Segmenter = (Intl as any).Segmenter;
  if (!Segmenter) return [...text];
  return Array.from(
    new Segmenter(undefined, { granularity: 'grapheme' }).segment(text) as Iterable<{
      segment: string;
    }>,
    (g) => g.segment
  );
}

const utf8Bytes = (text: string) =>
  [...text].reduce((n, ch) => {
    const c = ch.codePointAt(0) as number;
    return n + (c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4);
  }, 0);

/** Words as Threads counts them: its API reference counts an emoji as its UTF-8 bytes. */
function threadsLength(text: string): number {
  return graphemes(text).reduce(
    (n, g) =>
      n +
      (/\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(g) ? utf8Bytes(g) : [...g].length),
    0
  );
}

/** What else about the post changes its length. */
export type PostLengthContext = {
  /** A photo or video post, not a link preview or words alone. */
  media?: boolean;
  campaign?: string | null;
};

/**
 * A link with the tags the engine adds, a post's id included
 * (smagent-engine utils/trackedLink.ts).
 */
function taggedLink(link: string, source: string, campaign?: string | null): string {
  try {
    const url = new URL(link);
    const tags = {
      utm_source: source,
      utm_medium: 'social',
      utm_campaign: (campaign || '').trim() || 'tinycms',
      utm_content: '00000000-0000-0000-0000-000000000000',
    };
    Object.entries(tags).forEach(([key, value]) => {
      if (!url.searchParams.has(key)) url.searchParams.append(key, value);
    });
    return url.toString();
  } catch {
    return link;
  }
}

/** Characters the post takes on this network, link included the way it counts there. */
export function postLength(
  platform: string,
  text: string,
  link = '',
  { media, campaign }: PostLengthContext = {}
): number {
  const key = networkKey(platform);
  const l = link.trim();
  const tagged = () => taggedLink(l, key, campaign).length + 1;
  if (key === 'bluesky' || key === 'threads') {
    const words = key === 'bluesky' ? graphemes(text).length : threadsLength(text);
    return words + (l && media ? tagged() : 0);
  }
  if (key === 'x') return xLength(text) + (l ? 24 : 0);
  const chars = [...text].length;
  if (!l) return chars;
  if (key === 'instagram' || key === 'youtube' || key === 'tiktok') return chars + tagged();
  if (key === 'linkedin') return chars + (media ? tagged() : 0);
  if (key === 'facebook' && media) return chars;
  return chars + l.length + 1;
}

/** How many of the text's characters fit within the limit, link included. */
export function charsThatFit(
  platform: string,
  text: string,
  link = '',
  context: PostLengthContext = {},
  limit = postLimit(platform)
): number {
  const chars = [...text];
  if (limit == null) return chars.length;
  let fits = 0;
  let tooMany = chars.length + 1;
  while (tooMany - fits > 1) {
    const mid = Math.floor((fits + tooMany) / 2);
    if (postLength(platform, chars.slice(0, mid).join(''), link, context) <= limit) fits = mid;
    else tooMany = mid;
  }
  return fits;
}

/** The counter's colour: over the limit, nearly there, or fine. */
export function lengthTone(n: number, limit: number | null): 'over' | 'near' | 'ok' {
  if (limit == null) return 'ok';
  return n > limit ? 'over' : n > limit * 0.9 ? 'near' : 'ok';
}

/** A first version that fits ("Write an X version"): the text cut to leave room for the link. */
export function fitText(
  platform: string,
  text: string,
  link = '',
  context: PostLengthContext = {}
): string {
  const limit = postLimit(platform);
  if (limit == null || postLength(platform, text, link, context) <= limit) return text;
  const kept = charsThatFit(platform, text, link, context, limit - postLength(platform, '…'));
  return [...text].slice(0, kept).join('') + '…';
}
